import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY_IDS, driveImg } from "@/constants/galleryImages";
import { SectionHeading } from "./SectionHeading";
import { useT, useLang } from "@/lib/i18n";

const VISIBLE_STEP = 12;

export function Gallery() {
  const [visible, setVisible] = useState(VISIBLE_STEP);
  const [active, setActive] = useState(null); // index in GALLERY_IDS
  const t = useT();
  const { lang } = useLang();
  const isHi = lang === "hi";

  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(() => setActive((i) => (i - 1 + GALLERY_IDS.length) % GALLERY_IDS.length), []);
  const next = useCallback(() => setActive((i) => (i + 1) % GALLERY_IDS.length), []);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [active, close, prev, next]);

  const shown = GALLERY_IDS.slice(0, visible);

  return (
    <section id="gallery" className="relative py-16 md:py-24 border-t border-border">
      <div className="max-w-[90rem] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
          <SectionHeading
            index="—"
            overline={t("gallery.overline")}
            overlineClassName={isHi ? "font-hindi tracking-normal text-[12px]" : undefined}
            title={
              isHi ? (
                <span className="font-hindi">{t("gallery.titleA")}<span className="font-hindi">{t("gallery.titleB")}</span></span>
              ) : (
                <>{t("gallery.titleA")}<span className="font-display italic font-normal">{t("gallery.titleB")}</span></>
              )
            }
          />
          <span className={`text-sm text-muted-foreground shrink-0 ${isHi ? "font-hindi" : ""}`}>{GALLERY_IDS.length}+ {t("gallery.moments")}</span>
        </div>

        {/* Masonry-ish responsive grid */}
        <div className="columns-2 md:columns-3 lg:columns-4 gap-3 md:gap-4 [column-fill:_balance]">
          {shown.map((id, i) => (
            <motion.button
              key={id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % VISIBLE_STEP) * 0.03, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => setActive(i)}
              className="mb-3 md:mb-4 block w-full overflow-hidden rounded-xl border border-border group relative break-inside-avoid"
              aria-label={`Open photo ${i + 1}`}
            >
              <img
                src={driveImg(id, 800)}
                alt={`KIKI campus life ${i + 1}`}
                loading="lazy"
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </motion.button>
          ))}
        </div>

        {visible < GALLERY_IDS.length && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => setVisible((v) => Math.min(v + VISIBLE_STEP, GALLERY_IDS.length))}
              className={`btn-ghost-pill ${isHi ? "font-hindi" : ""}`}
            >
              {t("gallery.loadMore")}
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[120] bg-black/92 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={close}
          >
            <button onClick={close} aria-label="Close" className="absolute top-5 right-5 w-11 h-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors">
              <X className="w-5 h-5" strokeWidth={1.8} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous" className="absolute left-4 md:left-8 w-11 h-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors">
              <ChevronLeft className="w-5 h-5" strokeWidth={1.8} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next" className="absolute right-4 md:right-8 w-11 h-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors">
              <ChevronRight className="w-5 h-5" strokeWidth={1.8} />
            </button>
            <motion.img
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              src={driveImg(GALLERY_IDS[active], 1600)}
              alt={`KIKI campus life ${active + 1}`}
              className="max-h-[85vh] max-w-[92vw] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-5 left-0 right-0 text-center label-overline text-white/70">
              {active + 1} / {GALLERY_IDS.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
